import { BaseApiClient } from './base-client'
import {
  type ApiResponse,
  type ListingCsvRow,
  API_ROUTES,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'

/**
 * Flat upload response. The API's ObservabilityInterceptor unwraps controller
 * returns, so the payload arrives under `ApiResponse.data` as a flat object
 * (not a nested `{ success, data }`).
 */
export interface UploadListingsCsvResult {
  success: boolean
  deletedCount: number
  createdCount: number
  ids: string[]
  message: string
}

/**
 * Client for manual (CSV) listing management. CSV upload is an alternative,
 * mutually-exclusive sync mode: uploading replaces all listings and stops any
 * scheduled URL sync / conflicting PMS listing sync.
 */
export class ListingsApiClient extends BaseApiClient {
  /** Upload listings from a CSV for the current company. */
  async uploadListingsCsv(
    listings: ListingCsvRow[],
  ): Promise<UploadListingsCsvResult | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<UploadListingsCsvResult>
        >(API_ROUTES.LISTINGS.CSV_UPLOAD, {
          method: 'POST',
          body: JSON.stringify({ listings }),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-rethrow',
        errorContext: 'Error uploading listings CSV',
      },
    )
  }

  /** Upload listings from a CSV for a specific company (Super Admin only). */
  async uploadListingsCsvForCompany(
    companyId: string,
    listings: ListingCsvRow[],
  ): Promise<UploadListingsCsvResult | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<UploadListingsCsvResult>
        >(`${API_ROUTES.ADMIN.LISTINGS.CSV_UPLOAD}?companyId=${companyId}`, {
          method: 'POST',
          body: JSON.stringify({ listings }),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-rethrow',
        errorContext: 'Error uploading listings CSV for company',
      },
    )
  }
}

export const listingsApiClient = new ListingsApiClient()
